import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useVersionCheck } from '../hooks/useVersionCheck';

// 새 배포 감지 + dirty 폼 추적 + 안전한 시점 자동 reload 를 담당한다.
// - 새 버전이 감지되면 hasUpdate 를 true 로 올린다.
// - 라우트가 바뀌는 순간 dirty 폼이 없으면 조용히 window.location.reload() 한다.
// - 사용자가 IDLE_TIMEOUT_MS 이상 입력이 없고 dirty 폼이 없으면 조용히 reload 한다.
// - dirty 폼이 있으면 reload 를 미루고, 우하단 알림은 계속 떠 있게 둔다.

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5분
const IDLE_CHECK_INTERVAL_MS = 30 * 1000; // 30초마다 확인

const UpdateContext = createContext(null);

export const UpdateProvider = ({ children }) => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const dirtyFormsRef = useRef(new Set());
  const lastActivityRef = useRef(Date.now());
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const registerDirty = useCallback(id => {
    dirtyFormsRef.current.add(id);
  }, []);

  const unregisterDirty = useCallback(id => {
    dirtyFormsRef.current.delete(id);
  }, []);

  const handleNewVersion = useCallback(() => {
    setHasUpdate(true);
  }, []);

  useVersionCheck(handleNewVersion);

  // 라우트 이동 시점에 dirty 폼이 없으면 조용히 새 버전 적용
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;
    if (hasUpdate && dirtyFormsRef.current.size === 0) {
      window.location.reload();
    }
  }, [location.pathname, hasUpdate]);

  // 사용자 활동 감지 (마우스 / 키보드 / 클릭)
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const opts = { passive: true };
    window.addEventListener('mousemove', updateActivity, opts);
    window.addEventListener('keydown', updateActivity, opts);
    window.addEventListener('click', updateActivity, opts);
    window.addEventListener('scroll', updateActivity, opts);
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  // hasUpdate 가 켜진 동안 주기적으로 idle 시간 체크 → 충분히 idle + dirty 폼 없으면 자동 reload
  useEffect(() => {
    if (!hasUpdate) return;
    const intervalId = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;
      if (idleTime >= IDLE_TIMEOUT_MS && dirtyFormsRef.current.size === 0) {
        window.location.reload();
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [hasUpdate]);

  const value = useMemo(
    () => ({
      hasUpdate,
      registerDirty,
      unregisterDirty,
      hasDirtyForms: () => dirtyFormsRef.current.size > 0,
    }),
    [hasUpdate, registerDirty, unregisterDirty],
  );

  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>;
};

export const useUpdate = () => {
  const ctx = useContext(UpdateContext);
  if (!ctx) {
    // Provider 밖에서 호출되어도 앱이 깨지지 않게 no-op 폴백 반환
    return {
      hasUpdate: false,
      registerDirty: () => {},
      unregisterDirty: () => {},
      hasDirtyForms: () => false,
    };
  }
  return ctx;
};

/**
 * RHF 의 formState.isDirty 를 전역 dirty 카운트에 등록한다.
 * 사용 예: useDirtyTracker(formState.isDirty);
 */
export const useDirtyTracker = isDirty => {
  const { registerDirty, unregisterDirty } = useUpdate();
  const idRef = useRef(`form-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const id = idRef.current;
    if (isDirty) {
      registerDirty(id);
    } else {
      unregisterDirty(id);
    }
    return () => unregisterDirty(id);
  }, [isDirty, registerDirty, unregisterDirty]);
};
